import type {
  BulkUpdateVariantPricesDTO,
  CreateCategoryDTO,
  CreateProductDTO,
  CreateVariantDTO,
  DuplicateConvertBatchDTO,
  DuplicateConvertDTO,
  DuplicateConvertPreviewDTO,
  SyncProductVariantsDTO,
  UpdateCategoryDTO,
  UpdateProductDTO,
  UpdateVariantDTO,
} from "@/modules/products/domain/contracts";
import type { VariantDeletePreview } from "@/modules/products/domain/repo/variant-delete";
import type {
  BulkUpdateVariantPricesResultDTO,
  CategoriesListResult,
  DuplicateConvertBatchResultDTO,
  DuplicateConvertPreviewResultDTO,
  DuplicateConvertResultDTO,
  ProductDetailResult,
  ProductPickerResult,
  ProductsListResult,
  VariantPriceEditListResult,
} from "@/modules/products/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type ProductListQuery = {
  categoryId?: string;
  search?: string;
  lowStock?: boolean;
  includeInactive?: boolean;
};

export type VariantPriceEditListQuery = {
  categoryId?: string;
  search?: string;
  includeInactive?: boolean;
};

export const productsApi = {
  async listCategories(): Promise<CategoriesListResult> {
    return fetcher.get(`${config.apiUrl}/products/categories`);
  },
  async createCategory(input: CreateCategoryDTO) {
    return fetcher.post(`${config.apiUrl}/products/categories`, input);
  },
  async updateCategory(id: string, input: UpdateCategoryDTO) {
    return fetcher.patch(`${config.apiUrl}/products/categories/${id}`, input);
  },
  async list(query?: ProductListQuery): Promise<ProductsListResult> {
    const url = new URL(`${config.apiUrl}/products`);
    if (query?.categoryId) url.searchParams.set("categoryId", query.categoryId);
    if (query?.search) url.searchParams.set("search", query.search);
    if (query?.lowStock) url.searchParams.set("lowStock", "true");
    if (query?.includeInactive) url.searchParams.set("includeInactive", "true");
    return fetcher.get(url.toString());
  },
  async exportCsv(query?: ProductListQuery): Promise<Blob> {
    const url = new URL(`${config.apiUrl}/products/export`);
    if (query?.categoryId) url.searchParams.set("categoryId", query.categoryId);
    if (query?.search) url.searchParams.set("search", query.search);
    if (query?.lowStock) url.searchParams.set("lowStock", "true");
    if (query?.includeInactive) url.searchParams.set("includeInactive", "true");
    const res = await fetch(url.toString(), { credentials: "include" });
    if (!res.ok) throw new Error("EXPORT_FAILED");
    return res.blob();
  },
  async picker(query?: {
    categoryId?: string;
    search?: string;
    purpose?: "order" | "receive";
    excludeOrderId?: string;
  }) {
    const url = new URL(`${config.apiUrl}/products/picker`);
    if (query?.categoryId) url.searchParams.set("categoryId", query.categoryId);
    if (query?.search) url.searchParams.set("search", query.search);
    if (query?.purpose) url.searchParams.set("purpose", query.purpose);
    if (query?.excludeOrderId) {
      url.searchParams.set("excludeOrderId", query.excludeOrderId);
    }
    return fetcher.get<ProductPickerResult>(url.toString());
  },
  async get(id: string): Promise<ProductDetailResult> {
    return fetcher.get(`${config.apiUrl}/products/${id}`);
  },
  async create(input: CreateProductDTO): Promise<ProductDetailResult> {
    return fetcher.post(`${config.apiUrl}/products`, input);
  },
  async update(
    id: string,
    input: UpdateProductDTO,
  ): Promise<ProductDetailResult> {
    return fetcher.patch(`${config.apiUrl}/products/${id}`, input);
  },
  async syncVariants(
    productId: string,
    input: SyncProductVariantsDTO,
  ): Promise<ProductDetailResult> {
    return fetcher.put(
      `${config.apiUrl}/products/${productId}/variants`,
      input,
    );
  },
  async createVariant(
    productId: string,
    input: CreateVariantDTO,
  ): Promise<ProductDetailResult> {
    return fetcher.post(
      `${config.apiUrl}/products/${productId}/variants`,
      input,
    );
  },
  async updateVariant(
    variantId: string,
    input: UpdateVariantDTO,
  ): Promise<ProductDetailResult> {
    return fetcher.patch(
      `${config.apiUrl}/products/variants/${variantId}`,
      input,
    );
  },
  async getVariantDeletePreview(
    variantId: string,
  ): Promise<VariantDeletePreview> {
    return fetcher.get(
      `${config.apiUrl}/products/variants/${variantId}/delete-preview`,
    );
  },
  async deleteVariant(
    variantId: string,
  ): Promise<{ ok: true; product: ProductDetailResult | null }> {
    return fetcher.delete(
      `${config.apiUrl}/products/variants/${variantId}`,
    );
  },
  async duplicateConvertPreview(
    input: DuplicateConvertPreviewDTO,
  ): Promise<DuplicateConvertPreviewResultDTO> {
    return fetcher.post(
      `${config.apiUrl}/products/duplicate-convert/preview`,
      input,
    );
  },
  async duplicateConvert(
    input: DuplicateConvertDTO,
  ): Promise<DuplicateConvertResultDTO> {
    return fetcher.post(`${config.apiUrl}/products/duplicate-convert`, input);
  },
  async duplicateConvertBatch(
    input: DuplicateConvertBatchDTO,
  ): Promise<DuplicateConvertBatchResultDTO> {
    return fetcher.post(
      `${config.apiUrl}/products/duplicate-convert/batch`,
      input,
    );
  },
  async adjustStock(
    variantId: string,
    input: {
      quantityDelta: number;
      note?: string;
      lotPicks?: { stockLotId: string; quantity: number }[];
    },
  ) {
    return fetcher.post(
      `${config.apiUrl}/products/variants/${variantId}/adjust-stock`,
      input,
    );
  },
  async listVariantsForPriceEdit(
    query?: VariantPriceEditListQuery,
  ): Promise<VariantPriceEditListResult> {
    const url = new URL(`${config.apiUrl}/products/variants/for-price-edit`);
    if (query?.categoryId) url.searchParams.set("categoryId", query.categoryId);
    if (query?.search) url.searchParams.set("search", query.search);
    if (query?.includeInactive) {
      url.searchParams.set("includeInactive", "true");
    }
    return fetcher.get(url.toString());
  },
  async bulkUpdateVariantPrices(
    input: BulkUpdateVariantPricesDTO,
  ): Promise<BulkUpdateVariantPricesResultDTO> {
    return fetcher.patch(
      `${config.apiUrl}/products/variants/bulk-prices`,
      input,
    );
  },
};
